import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveDate,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateManufacturedGLAAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];

  if (
    !subjectImprovements ||
    !compImprovements ||
    isUndefined(depreciationRatio) ||
    !effectiveDate
  ) {
    return undefined;
  }

  const { gla, craftsmanQuality: quality } = subjectImprovements;
  const { gla: compGla } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: gla ? AloftArea.schema.parse(gla).toSqFt().unitAmount : undefined,
      compUnitAmount: compGla ? AloftArea.schema.parse(compGla).toSqFt().unitAmount : undefined,
    });
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);
  if (!gla || !compGla || isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1) {
    return undefined;
  }

  return adjCalculator.getManufacturedGLAAdj({
    subject: { quality, sqft: AloftArea.schema.parse(gla) },
    comp: { sqft: AloftArea.schema.parse(compGla) },
    depreciationRatio,
  }).adjustment;
};

export const selectManufacturedGLAAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (_state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'mainDwelling'),
  ],
  calculateManufacturedGLAAdj,
);

export const selectManufacturedGLAAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'mainDwelling'),
  ],
  (
    adjustmentProps,
    improvements,
    depreciationRatio,
    effectiveDate,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      if (improvements[propID]?.propertyType !== 'manufacturedHome') return acc;

      const adj = calculateManufacturedGLAAdj(
        adjustmentProps,
        improvements,
        depreciationRatio,
        effectiveDate,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedManufacturedGLAAdj = createPriceSelector(
  [selectManufacturedGLAAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'mainDwelling', adjSettings),
);

export const selectAppliedManufacturedGLAAdjDisplay = createSelector(
  [selectAppliedManufacturedGLAAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);
